import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useCreateStore } from '@/stores/create';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import type { AuthorityService } from '@/types/enums/AuthorityService';
import type { SignatureModeDraftSnapshot } from '@/types/create/WorkflowDraft';

export const useSignatureModeDraft = defineStore('signatureModeDraft', () => {
  const createStore = useCreateStore();

  const signatureMode = ref<SignatureMode>(SignatureMode.TIMESTAMP);
  const jurisdiction = ref<Jurisdiction>(Jurisdiction.ZERTES);
  const enforceAuthorityService = ref(false);
  const authorityService = ref<AuthorityService | null>(null);

  const initialSnapshot = ref<SignatureModeDraftSnapshot | null>(null);

  const isDirty = computed(() => {
    if (!initialSnapshot.value) return false;
    return (
      signatureMode.value !== initialSnapshot.value.signatureMode ||
      jurisdiction.value !== initialSnapshot.value.jurisdiction ||
      enforceAuthorityService.value !==
        initialSnapshot.value.enforceAuthorityService ||
      authorityService.value !== initialSnapshot.value.authorityService
    );
  });

  function snapshot(): SignatureModeDraftSnapshot {
    return {
      signatureMode: signatureMode.value,
      jurisdiction: jurisdiction.value,
      enforceAuthorityService: enforceAuthorityService.value,
      authorityService: authorityService.value
    };
  }

  function initFromDocument() {
    const doc = createStore.document;
    if (!doc) return;

    signatureMode.value = doc.signatureMode ?? SignatureMode.TIMESTAMP;
    jurisdiction.value = doc.jurisdiction ?? Jurisdiction.ZERTES;
    enforceAuthorityService.value = !!doc.requiredAuthorityService;
    authorityService.value = doc.requiredAuthorityService ?? null;
    initialSnapshot.value = snapshot();
  }

  function discard() {
    if (!initialSnapshot.value) return;
    signatureMode.value = initialSnapshot.value.signatureMode;
    jurisdiction.value = initialSnapshot.value.jurisdiction;
    enforceAuthorityService.value =
      initialSnapshot.value.enforceAuthorityService;
    authorityService.value = initialSnapshot.value.authorityService;
  }

  function validate() {
    return { valid: true };
  }

  async function reconcile() {
    const doc = createStore.document;
    if (!doc) return;

    const modeChanged =
      signatureMode.value !== doc.signatureMode ||
      jurisdiction.value !== doc.jurisdiction;
    const authorityChanged =
      (enforceAuthorityService.value ? authorityService.value : null) !==
      (doc.requiredAuthorityService ?? null);

    if (modeChanged) {
      await createStore.updateSignatureLevel({
        level: signatureMode.value,
        newJurisdiction: jurisdiction.value
      });
    }

    if (authorityChanged) {
      await createStore.enforceAuthorityService(
        enforceAuthorityService.value ? authorityService.value : null
      );
    }

    initialSnapshot.value = snapshot();
  }

  function setSignatureMode(mode: SignatureMode) {
    signatureMode.value = mode;
    if (mode === SignatureMode.TIMESTAMP) {
      enforceAuthorityService.value = false;
      authorityService.value = null;
    }
  }

  function setJurisdiction(value: Jurisdiction) {
    jurisdiction.value = value;
    if (
      value === Jurisdiction.EIDAS &&
      signatureMode.value === SignatureMode.ADVANCED
    ) {
      enforceAuthorityService.value = false;
      authorityService.value = null;
    }
  }

  function setEnforceAuthorityService(value: boolean) {
    enforceAuthorityService.value = value;
    if (!value) {
      authorityService.value = null;
    }
  }

  function setAuthorityService(service: AuthorityService | null) {
    authorityService.value = service;
  }

  return {
    signatureMode,
    jurisdiction,
    enforceAuthorityService,
    authorityService,
    isDirty,
    initFromDocument,
    discard,
    validate,
    reconcile,
    setSignatureMode,
    setJurisdiction,
    setEnforceAuthorityService,
    setAuthorityService
  };
});
